import React, {useEffect, useState} from "react";
import {useBuildingKey} from "../../Helpers/utils";
import {get, getDatabase, ref} from "firebase/database";
import BuildingImagesSection from "./BuildingImagesSection";
import ClickableImage from "../../Components/ClickableImage";
import {Helmet} from "react-helmet-async";
import ZoomableImage from "../../Helpers/ZoomableImage";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {BaseComponent, BuildingModel} from "../../types/protocols";
import {translate} from "../../types/EnumTranslations";
import {BaseType} from "../../types/Enums";

interface BuildingImagesListProps {
    buildingData?: BuildingModel;
}

const BuildingImagesList: React.FC<BuildingImagesListProps> = ({ buildingData: propBuilding }) => {
    const [building, setBuilding] = useState<BuildingModel | null>(propBuilding || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const buildingKey = useBuildingKey();
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined)
    const [selectedImageKey, setSelectedImageKey] = useState<string | undefined>(undefined)

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const db = getDatabase();
                let buildingRef = ref(db, `buildings/${buildingKey}`);
                const snapshot = await get(buildingRef);
                if (snapshot.exists()) {
                    setBuilding(snapshot.val() as BuildingModel);
                    setError(null);
                } else {
                    setError("Building not found");
                    setBuilding(null);
                }
            } catch (err) {
                setError("Failed to load component data");
                setBuilding(null);
            } finally {
                setLoading(false);
            }
        };

        if (!building) {
            fetchData().then();
        }
    }, [building, buildingKey]);

    if (loading) return <p></p>;
    if (error) return <p style={{ color: "#ff0000" }}>{error}</p>;
    if (!building) return <p>No building data available</p>;
    if (!building.structure) return <p>No structure data</p>;
    if (!buildingKey) return <p>Byggnadsnyckel saknas</p>;
    const { structure } = building;

    const filterComponentsByType = (components: { [key: string]: BaseComponent } = {}, type: BaseType) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
        );

    const filterComponentsByRoomAndType = (components: { [key: string]: BaseComponent } = {}, type: BaseType, roomKey: string) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
                .filter(([_, component]) => component.roomKey === roomKey)
        );

    return (
        <div style={{
            backgroundColor: "#f4f4f4",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <Helmet>
                <title>Samtliga bilder: {"building.name"}</title>
            </Helmet>

            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={[
                        {text: "building.name" ?? 'Byggnad', link: `/building/${buildingKey}`},
                        {text: "Samtliga bilder"}
                    ]}
                />
            </div>

            <div style={{
                display: "flex",
                flex: 1,
                overflow: "hidden",
            }}>
                {/* Left Pane */}
                <div
                    style={{
                        flex: "0 0 min(40%, 800px)",
                        overflow: 'auto',
                        padding: '20px',
                        paddingBottom: '40px',
                    }}
                >
                    <div style={{display: 'flex', gap: '40px', flexDirection: 'column'}}>
                        <BuildingImagesSection
                            title={translate(BaseType.Roof)}
                            components={filterComponentsByType(structure.components, BaseType.Roof)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Facade)}
                            components={filterComponentsByType(structure.components, BaseType.Facade)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Foundation)}
                            components={filterComponentsByType(structure.components, BaseType.Foundation)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Chimney)}
                            components={filterComponentsByType(structure.components, BaseType.Chimney)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Drainage)}
                            components={filterComponentsByType(structure.components, BaseType.Drainage)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Utility)}
                            components={filterComponentsByType(structure.components, BaseType.Utility)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Outdoor)}
                            components={filterComponentsByType(structure.components, BaseType.Outdoor)}
                            buildingKey={buildingKey}
                            onImageClick={(photo, url)=> {
                                setSelectedImageKey(photo.key)
                                setImageUrl(url)
                            }}
                            selectedImageKey={selectedImageKey}
                        />

                        {structure.rooms &&
                            Object.values(structure.rooms).map((room, index) => (
                                <div key={index} >
                                    <h3 key={room.key}>{room.label ?? translate(room.type)}</h3>

                                    {room.photos ? (
                                        <div
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "40px",
                                                borderRadius: "15px",
                                                padding: "30px",
                                                backgroundColor: "#fff",
                                                textAlign: "left"
                                            }}
                                        >
                                            <div>
                                                <h4>Översiktsbilder</h4>
                                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                    {Object.values(room.photos).map((photo) => (
                                                        <ClickableImage
                                                            key={photo.key || photo.smallSizeUrl}
                                                            photo={photo}
                                                            buildingKey={buildingKey}
                                                            showDate={true}
                                                            onImageClick={(photo, url)=> {
                                                                setSelectedImageKey(photo.key)
                                                                setImageUrl(url)
                                                            }}
                                                            highlighted={selectedImageKey === photo.key}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            <BuildingImagesSection
                                                title="Innerväggar"
                                                components={filterComponentsByRoomAndType(structure.components, BaseType.Wall, room.key)}
                                                buildingKey={buildingKey}
                                                onImageClick={(photo, url)=> {
                                                    setSelectedImageKey(photo.key)
                                                    setImageUrl(url)
                                                }}
                                                selectedImageKey={selectedImageKey}
                                                useDivider={true}
                                            />

                                            <BuildingImagesSection
                                                title="Innertak"
                                                components={filterComponentsByRoomAndType(structure.components, BaseType.Ceiling, room.key)}
                                                buildingKey={buildingKey}
                                                onImageClick={(photo, url)=> {
                                                    setSelectedImageKey(photo.key)
                                                    setImageUrl(url)
                                                }}
                                                selectedImageKey={selectedImageKey}
                                                useDivider={true}
                                            />

                                            <BuildingImagesSection
                                                title="Innergolv"
                                                components={filterComponentsByRoomAndType(structure.components, BaseType.Floor, room.key)}
                                                buildingKey={buildingKey}
                                                onImageClick={(photo, url)=> {
                                                    setSelectedImageKey(photo.key)
                                                    setImageUrl(url)
                                                }}
                                                selectedImageKey={selectedImageKey}
                                                useDivider={true}
                                            />
                                        </div>
                                    ) : (
                                        <>Inga foton</>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>

                {/* Right Pane (fixed) */}
                <div
                    style={{
                        position: 'sticky',
                        right: 0,
                        top: 0,
                        width: "calc(100% - min(40%, 800px))",
                        height: "100vh",
                        backgroundColor: "#fff",
                        boxSizing: "border-box",
                    }}
                >
                    {
                        imageUrl ?
                            <ZoomableImage
                                src={imageUrl}
                            />
                            :
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%'
                            }}>
                                Klicka på en bild för att visa den här
                            </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default BuildingImagesList;